import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  CheckCircle, 
  Home, 
  Banknote, 
  Shield, 
  Users, 
  ArrowRight, 
  Phone, 
  Mail, 
  Calculator,
  Clock,
  TrendingUp,
  FileText
} from "lucide-react";
import janineHardiImage from "@/assets/janine-hardi.jpg";
import rueckmieteImage from "@/assets/rueckmiete.png";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import ProvenExpertBadge from "@/components/ProvenExpertBadge";

const Rueckmiete = () => {
  const benefits = [
    {
      icon: Banknote,
      title: "Komplette Liquidität",
      description: "Sie erhalten den gesamten Kaufpreis der Immobilie sofort ausgezahlt."
    },
    {
      icon: Shield,
      title: "Planbare Mietkosten",
      description: "Durch den Mietvertrag stehen die monatlichen Kosten von Anfang an fest – übersichtlich, kalkulierbar und ohne Überraschungen."
    },
    {
      icon: Home,
      title: "Keine Instandhaltungspflichten mehr",
      description: "Eigentümerpflichten wie Dachsanierung, Heizungsaustausch oder Modernisierung entfallen. Diese trägt künftig der neue Eigentümer."
    },
    {
      icon: ArrowRight,
      title: "Flexibilität",
      description: "Wer später einen Umzug plant – zum Beispiel ins Pflegeheim oder in eine kleinere Wohnung – kann den Mietvertrag kündigen."
    },
    {
      icon: CheckCircle,
      title: "Sicherheit",
      description: "Das Mietrecht wird im Vertrag gesichert, sodass Sie nach dem Verkauf nicht ohne Zuhause dastehen."
    }
  ];

  const examples = [
    {
      title: "Haus in Frankfurt",
      value: "1.000.000 €",
      payout: "1.000.000 €",
      rent: "2.500 €",
      note: "Keine Instandhaltungskosten mehr"
    },
    {
      title: "Eigentumswohnung in Berlin",
      value: "500.000 €",
      payout: "500.000 €",
      rent: "1.250 €",
      note: "Nutzung: Finanzierung von Pflegeleistungen"
    },
    {
      title: "Paar mit Umzugswunsch",
      value: "600.000 €",
      payout: "600.000 €",
      rent: "1.500 €",
      note: "Mietvertrag für 3 Jahre, dann Umzug in eine Seniorenresidenz"
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Kostenlose Erstberatung",
      description: "Die Experten von renteplusimmobilie.de erklären alle Modelle der Immobilienverrentung und prüfen, ob Rückmiete die beste Lösung ist."
    },
    {
      step: "2", 
      title: "Immobilienbewertung",
      description: "Die Immobilie wird professionell bewertet, um einen marktgerechten Kaufpreis zu erzielen."
    },
    {
      step: "3",
      title: "Verkaufsabwicklung", 
      description: "Ein notarieller Kaufvertrag sichert den Eigentumsübergang. Parallel wird ein Mietvertrag abgeschlossen."
    },
    {
      step: "4",
      title: "Auszahlung des Kaufpreises",
      description: "Unmittelbar nach Vertragsabschluss fließt die vereinbarte Kaufsumme auf Ihr Konto."
    },
    {
      step: "5",
      title: "Anmietung & Sicherheit",
      description: "Sie bleiben Mieter in Ihrer Immobilie – mit klar geregelten Kosten und ohne Eigentümerpflichten."
    }
  ];

  const faqItems = [
    {
      question: "Wie lange kann ich wohnen bleiben?",
      answer: "So lange, wie es im Mietvertrag vereinbart ist – häufig unbefristet oder mit langfristigen Garantien."
    },
    {
      question: "Muss ich Instandhaltungskosten tragen?",
      answer: "Nein, diese trägt der neue Eigentümer."
    },
    {
      question: "Kann ich den Mietvertrag kündigen?",
      answer: "Ja, ein Umzug ist jederzeit mit Kündigungsfrist möglich."
    },
    {
      question: "Was passiert mit meinen Erben?",
      answer: "Die Immobilie gehört nicht mehr zur Erbmasse, Erben profitieren jedoch indirekt von Ihrer finanziellen Freiheit."
    },
    {
      question: "Ist die Miete sicher?",
      answer: "Ja, die Kosten sind klar geregelt und im Mietvertrag fixiert."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Rückmiete: Haus verkaufen & wohnen bleiben </title>
        <meta 
          name="description" 
          content="Rückmiete: Haus komplett verkaufen & als Mieter wohnen bleiben. ✓ Sofortige Auszahlung ✓ Planbare Mietkosten ✓ Kostenlose Beratung zur Eigenheimverrentung"
        />
        <meta name="keywords" content="rückmiete, haus verkaufen und wohnen bleiben, verkaufen aber wohnen bleiben, verkaufenundbleiben, immobilie verrenten, eigenheimverrentung" />
        <link rel="canonical" href="https://eigenheimverrentung.de/rueckmiete" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Rückmiete: Haus verkaufen & wohnen bleiben" />
        <meta property="og:description" content="Verkaufen Sie Ihr Haus komplett und bleiben als Mieter wohnen. Sofortige Auszahlung, planbare Mietkosten. Kostenlose Beratung." />
        <meta property="og:url" content="https://eigenheimverrentung.de/rueckmiete" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Rückmiete: Haus verkaufen & wohnen bleiben" />
        <meta name="twitter:description" content="Verkaufen Sie Ihr Haus komplett und bleiben als Mieter wohnen. Sofortige Auszahlung, planbare Mietkosten." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Rückmiete – Haus verkaufen und als Mieter wohnen bleiben",
            "description": "Kompletter Ratgeber zur Rückmiete: Verkaufen Sie Ihr Haus komplett und bleiben als Mieter wohnen mit planbaren Kosten.",
            "author": {
              "@type": "Person",
              "name": "Janine Hardi",
              "jobTitle": "Rechtsanwältin & Gründerin",
              "worksFor": {
                "@type": "Organization",
                "name": "RentePlusImmobilie.de",
                "url": "https://eigenheimverrentung.de"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de",
              "url": "https://eigenheimverrentung.de"
            },
            "datePublished": "2026-04-01",
            "dateModified": "2026-04-08",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://eigenheimverrentung.de/rueckmiete"
            }
          })}
        </script>
        
        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Wie lange kann ich wohnen bleiben?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "So lange, wie es im Mietvertrag vereinbart ist – häufig unbefristet oder mit langfristigen Garantien."
                }
              },
              {
                "@type": "Question",
                "name": "Muss ich Instandhaltungskosten tragen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nein, diese trägt der neue Eigentümer."
                }
              },
              {
                "@type": "Question",
                "name": "Kann ich den Mietvertrag kündigen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, ein Umzug ist jederzeit mit Kündigungsfrist möglich."
                }
              },
              {
                "@type": "Question",
                "name": "Was passiert mit meinen Erben?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Die Immobilie gehört nicht mehr zur Erbmasse, Erben profitieren jedoch indirekt von Ihrer finanziellen Freiheit."
                }
              },
              {
                "@type": "Question",
                "name": "Ist die Miete sicher?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, die Kosten sind klar geregelt und im Mietvertrag fixiert."
                }
              }
            ]
          })}
        </script>

        {/* Organization & Local Business Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "RentePlusImmobilie - Eigenheimverrentung",
            "url": "https://eigenheimverrentung.de",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "areaServed": "Deutschland",
            "serviceType": ["Leibrente", "Wohnrecht", "Nießbrauchrecht", "Teilverkauf", "Rückmiete", "Seniorenfinanzierung"],
            "description": "Kostenlose Beratung zur Eigenheimverrentung mit Rückmiete - Haus verkaufen und als Mieter wohnen bleiben."
          })}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Startseite",
                "item": "https://eigenheimverrentung.de/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Rückmiete",
                "item": "https://eigenheimverrentung.de/rueckmiete"
              }
            ]
          })}
        </script>

        {/* HowTo Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Wie funktioniert Rückmiete?",
            "description": "Schritt-für-Schritt Anleitung zur Rückmiete - Haus verkaufen und als Mieter wohnen bleiben",
            "totalTime": "P30D",
            "step": processSteps.map((step, index) => ({
              "@type": "HowToStep",
              "position": index + 1,
              "name": step.title,
              "text": step.description
            }))
          })}
        </script>

        {/* Service Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Rückmiete",
            "name": "Rückmiete - Komplette Liquidität mit Wohnrecht",
            "description": "Verkauf der Immobilie mit anschließender Anmietung. Sofortige Auszahlung des gesamten Kaufpreises bei planbaren Mietkosten.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "RentePlusImmobilie - Eigenheimverrentung",
              "url": "https://eigenheimverrentung.de",
              "telephone": "+49-89-628246446",
              "email": "info@renteplusimmobilie.de"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Senioren mit hohem Kapitalbedarf oder Umzugswunsch"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Rückmiete Leistungen",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Sofortige Auszahlung",
                    "description": "Kompletter Kaufpreis wird sofort ausgezahlt"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Planbare Mietkosten",
                    "description": "Feste monatliche Kosten ohne Überraschungen"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Keine Instandhaltungspflichten",
                    "description": "Eigentümerpflichten entfallen vollständig"
                  }
                }
              ]
            },
            "url": "https://eigenheimverrentung.de/rueckmiete"
          })}
        </script>
      </Helmet>

      <Header />

      <main className="min-h-screen">
        {/* Breadcrumb */}
        <nav className="container-comfortable py-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Startseite</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">Rückmiete</span>
        </nav>

        {/* Hero Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6">
                <Home className="w-4 h-4 mr-2" />
                Immobilienverrentung
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
                Rückmiete – Kostenlose Beratung
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-4 leading-relaxed">
                Verkauf der Immobilie mit anschließender Anmietung
              </p>

              <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <img src={janineHardiImage} alt="Janine Hardi" className="w-8 h-8 rounded-full object-cover" />
                  <span>Von <strong className="text-foreground">Janine Hardi</strong>, Rechtsanwältin & Gründerin von RentePlusImmobilie.de</span>
                </div>
                <span className="hidden sm:inline">·</span>
                <time dateTime="2026-04-01" className="hidden sm:inline">April 2026</time>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-lg px-8" onClick={() => window.open('tel:+4989628246446', '_self')}>
                  <Phone className="w-5 h-5 mr-2" />
                  Jetzt kostenlos beraten lassen
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="text-lg px-8"
                  onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Beratungstermin vereinbaren
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">
                Einleitung
              </h2>
              
              <div className="prose prose-lg max-w-none space-y-6">
                <p className="text-lg leading-relaxed">
                  Viele Senioren oder Immobilienbesitzer stehen irgendwann vor derselben Frage: 
                  Wie kann ich den Wert meiner Immobilie nutzen, ohne sofort mein Zuhause verlassen zu müssen?
                </p>
                
                <p className="text-lg leading-relaxed">
                  Eine der flexibelsten Lösungen lautet: <strong>Rückmiete</strong>.
                  Hierbei verkaufen Eigentümer ihre Immobilie vollständig, bleiben aber durch einen 
                  Mietvertrag weiterhin darin wohnen. Sie erhalten den vollen Kaufpreis und sichern 
                  sich gleichzeitig planbare Mietkosten.
                </p>
                
                <p className="text-lg leading-relaxed">
                  Die Plattform <strong>renteplusimmobilie.de</strong> bietet hierfür eine kostenlose 
                  und unabhängige Beratung an. Ziel ist es, Eigentümern eine fundierte Entscheidungshilfe 
                  zu geben, ob die Rückmiete oder ein alternatives Verrentungsmodell besser passt.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Image Section */}
        <section className="py-12 md:py-16">
          <div className="container-comfortable">
            <div className="max-w-2xl mx-auto text-center">
              <img 
                src={rueckmieteImage} 
                alt="Rückmiete - Haus verkaufen und wohnen bleiben" 
                className="w-full max-w-md mx-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* Was ist Rückmiete */}
        <section className="py-16">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">
                Was ist eine Rückmiete?
              </h2>
              
              <Card className="mb-8">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed mb-6">
                    Bei der Rückmiete handelt es sich um eine Form des Immobilienverkaufs, 
                    die insbesondere für Eigentümer mit hohem Kapitalbedarf oder Umzugswunsch interessant ist.
                  </p>
                  
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                      <span>Der Eigentümer verkauft die gesamte Immobilie.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                      <span>Im gleichen Schritt schließt er einen Mietvertrag ab und bleibt als Mieter in seiner vertrauten Immobilie wohnen.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                      <span>Er erhält komplette Liquidität, da der gesamte Verkaufserlös sofort ausgezahlt wird.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                      <span>Statt Eigentümerpflichten trägt er nur noch die Mietkosten, die von Anfang an klar kalkulierbar sind.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <h3 className="text-2xl font-bold text-primary mb-6">
                Abgrenzung zu anderen Verrentungsmodellen
              </h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Leibrente</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">Verkauf gegen monatliche Zahlungen – Rückmiete dagegen bringt sofort den vollen Kaufpreis.</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Teilverkauf</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">Verkauf nur eines Teils – bei Rückmiete wird die gesamte Immobilie verkauft.</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Umkehrhypothek</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">Kredit auf die Immobilie – Rückmiete ist ein klassischer Verkauf ohne Darlehen.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Vorteile */}
        <section className="py-16 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12">
                Vorteile der Rückmiete
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="card-elevated">
                    <CardHeader>
                      <div className="bg-accent/10 p-3 rounded-xl w-fit mb-4">
                        <benefit.icon className="h-6 w-6 text-accent" />
                      </div>
                      <CardTitle>{index + 1}. {benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p>{benefit.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Für wen eignet sich Rückmiete */}
        <section className="py-16">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">
                Für wen eignet sich die Rückmiete?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Users className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Senioren mit hohem Kapitalbedarf</h3>
                        <p className="text-sm text-muted-foreground">Die schnell und vollständig Liquidität benötigen.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <ArrowRight className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Menschen mit Umzugswunsch</h3>
                        <p className="text-sm text-muted-foreground">Die flexibel bleiben wollen.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Home className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Eigentümer ohne Instandhaltungswunsch</h3>
                        <p className="text-sm text-muted-foreground">Die keine Instandhaltungspflichten mehr tragen wollen.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Familien ohne Erben</h3>
                        <p className="text-sm text-muted-foreground">Für die die Immobilie nicht im Nachlass erhalten bleiben muss.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Ablauf */}
        <section className="py-16 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12">
                Der Ablauf bei renteplusimmobilie.de
              </h2>
              
              <div className="space-y-8">
                {processSteps.map((step, index) => (
                  <Card key={index} className="card-elevated">
                    <CardContent className="p-8">
                      <div className="flex gap-6">
                        <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0">
                          {step.step}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-primary mb-3">{step.title}</h3>
                          <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Beispiele */}
        <section className="py-16">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12">
                Beispielrechnungen & Praxisfälle
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {examples.map((example, index) => (
                  <Card key={index} className="card-elevated">
                    <CardHeader>
                      <CardTitle className="text-xl">Beispiel {index + 1}: {example.title}</CardTitle>
                      <CardDescription>Wert: {example.value}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span>Sofortige Auszahlung:</span>
                        <strong>{example.payout}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>Monatliche Miete:</span>
                        <strong>{example.rent}</strong>
                      </div>
                      <Separator />
                      <p className="text-sm text-muted-foreground pt-2">
                        {example.note}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12">
                Häufige Fragen zur Rückmiete (FAQ)
              </h2>
              
              <div className="space-y-6">
                {faqItems.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <h3 className="font-bold text-primary mb-3">{faq.question}</h3>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Warum renteplusimmobilie.de */}
        <section className="py-16">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              {/* Founder Image */}
              <div className="text-center mb-8">
                <img 
                  src={janineHardiImage} 
                  alt="Janine Hardi - Gründerin von RentePlusImmobilie" 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto shadow-lg"
                />
                <p className="text-sm text-muted-foreground mt-3">Janine Hardi, Gründerin von RentePlusImmobilie</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
                Warum RentePlusImmobilie
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="card-elevated">
                  <CardHeader>
                    <div className="bg-accent/10 p-3 rounded-xl w-fit mb-4">
                      <Shield className="h-6 w-6 text-accent" />
                    </div>
                    <CardTitle>Unabhängige Experten</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Das Team berät neutral zu allen Verrentungsmodellen, nicht nur zur Rückmiete.</p>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <div className="bg-accent/10 p-3 rounded-xl w-fit mb-4">
                      <CheckCircle className="h-6 w-6 text-accent" />
                    </div>
                    <CardTitle>Kostenlose Beratung</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Keine Gebühren, keine versteckten Kosten - nur ehrliche und kompetente Beratung.</p>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <div className="bg-accent/10 p-3 rounded-xl w-fit mb-4">
                      <Users className="h-6 w-6 text-accent" />
                    </div>
                    <CardTitle>Individuelle Lösungen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Jeder Vertrag wird maßgeschneidert und an Ihre persönliche Situation angepasst.</p>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <div className="bg-accent/10 p-3 rounded-xl w-fit mb-4">
                      <TrendingUp className="h-6 w-6 text-accent" />
                    </div>
                    <CardTitle>Vertrauen & Erfahrung</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>Zahlreiche erfolgreiche Abschlüsse und langjährige Erfahrung in ganz Deutschland.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary/5">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">
                Mit Rückmiete flexibel und liquide bleiben
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Die Rückmiete ist eine ideale Lösung für Eigentümer, die Liquidität benötigen, 
                aber weiterhin in ihrer Immobilie wohnen möchten. Mit der kostenlosen Beratung von 
                renteplusimmobilie.de können Senioren sicherstellen, dass sie die für ihre Situation 
                beste Entscheidung treffen.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-lg px-8" onClick={() => window.open('tel:+4989628246446', '_self')}>
                  <Phone className="w-5 h-5 mr-2" />
                  089 628246446
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8" onClick={() => window.open('mailto:info@renteplusimmobilie.de', '_self')}>
                  <Mail className="w-5 h-5 mr-2" />
                  E-Mail senden
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <ProvenExpertBadge />
      <ContactSection />
      <HeyflowBanner />
    </>
  );
};

export default Rueckmiete;